import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Link as MuiLink, MenuItem, Paper, TextField, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { ItemService } from '../../services/item.service';
import { StockService } from '../../services/stock.service';
import { getStoresByInstitutionId } from '../../services/store.service';
import NotInChargeNotice from '../store/NotInChargeNotice';
import { Item } from '../../interfaces/item.interface';
import { Department } from '../../interfaces/department.interface';
import { DepartmentService } from '../../services/department.service';
import { EmployeeAutocomplete, EmployeeOption } from '../common/EmployeeAutocomplete';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { Store } from '../../interfaces/store.interface';
import { useToast } from '../../context/ToastContext';
import { TEXT_AREA_MAX_LENGTH } from '../../constants/fieldLimits';
import StockLotPicker from '../common/StockLotPicker';
import SearchableSelect from '../common/SearchableSelect';
import { helpWhy } from '../../help/why';
import OrderPicker from '../costing/OrderPicker';
import SerialNumbersField from '../serial/SerialNumbersField'; // F6b
import ItemScanField, { ScanOutcome } from './ItemScanField';   // Q3

const StockOut: React.FC = () => {
    const toast = useToast();
    const [items, setItems] = useState<Item[]>([]);
    const [stores, setStores] = useState<Store[]>([]);
    const [storesLoaded, setStoresLoaded] = useState(false);
    const [loading, setLoading] = useState(true);
    const [selectedItemId, setSelectedItemId] = useState<string>('');
    const [selectedStoreId, setSelectedStoreId] = useState<string>('');
    const [selectedItem, setSelectedItem] = useState<Item | null>(null);
    const [selectedStore, setSelectedStore] = useState<Store | null>(null);
    const [amount, setAmount] = useState<number>(0);
    const [remarks, setRemarks] = useState<string>('');
    const [currentStock, setCurrentStock] = useState<number | null>(null);
    const [loadingStock, setLoadingStock] = useState<boolean>(false);
    const [stockLotId, setStockLotId] = useState<string | null>(null);
    const [buyerOrderId, setBuyerOrderId] = useState<string>('');
    const [serials, setSerials] = useState<string[]>([]);   // F6b
    const [serialsNonce, setSerialsNonce] = useState(0);   // F6b: reloads the picker after a stock-out
    // Returnable items: who the item is issued to (an employee, a department, or both)
    const [holder, setHolder] = useState<EmployeeOption | null>(null);
    const [holderDepartmentId, setHolderDepartmentId] = useState('');
    const [departments, setDepartments] = useState<Department[]>([]);
    const canManageAssets = usePermission(PERMISSIONS.FIXED_ASSET_MANAGE);
    const isReturnable = selectedItem?.effectiveNature === 'RETURNABLE';
    const isFixedAsset = selectedItem?.effectiveNature === 'FIXED_ASSET';

    useEffect(() => {
        if (!isReturnable || departments.length > 0) return;
        DepartmentService.getLocationOptions().then(r => setDepartments(r.content ?? [])).catch(() => setDepartments([]));
    }, [isReturnable, departments.length]);

    useEffect(() => {
        fetchItems();
        fetchStores();
    }, []);

    const fetchItems = async () => {
        try {
            setLoading(true);
            const response = await ItemService.getItemsByInstitution(0, 100, '', 'STOCK'); // FW-2A
            setItems(response.content);
        } catch (err) {
            toast.showApiError(err, 'Failed to load items');
            console.error('Error fetching items:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchStores = async () => {
        try {
            setLoading(true);
            // Assuming you have a service to fetch stores
            const response = await getStoresByInstitutionId(); // Replace with actual service call
            setStores(response.content);
            setStoresLoaded(true);
        } catch (err) {
            toast.showApiError(err, 'Failed to load stores');
            console.error('Error fetching stores:', err);
        } finally {
            setLoading(false);
        }
    }

    // Function to fetch current stock amount when both store and item are selected
    const fetchCurrentStock = async () => {
        if (selectedStoreId && selectedItemId) {
            try {
                setLoadingStock(true);
                setCurrentStock(null);
                const stockData = await StockService.getStockByStoreAndItem(selectedStoreId, selectedItemId);
                setCurrentStock(stockData?.availableQty ?? 0);
            } catch (err) {
                console.error('Error fetching current stock:', err);
                setCurrentStock(0); // Default to 0 if there's an error or no stock exists
            } finally {
                setLoadingStock(false);
            }
        }
    };

    // Fetch current stock when store or item selection changes
    useEffect(() => {
        fetchCurrentStock();
    }, [selectedStoreId, selectedItemId]);

    const handleItemChange = (id: string) => {
        setSelectedItemId(id);

        const item = items.find(item => item.id === id);
        setSelectedItem(item || null);

        // Reset other fields
        setAmount(0);
        setStockLotId(null);
        setSerials([]);   // F6b
    };

    // ---- Q3: scan-to-add. Scanning picks the item; scanning the same item again adds 1 to the amount. ----
    const handleScan = (item: Item): ScanOutcome => {
        const label = `${item.itemCode} - ${item.itemName}`;
        if ((item.itemKind ?? 'STOCK') !== 'STOCK') {
            return { ok: false, message: `${label} is not a stock item, so it cannot be issued.` };
        }
        if (item.id === selectedItemId) {
            const next = amount + 1;
            if (currentStock !== null && next > currentStock) {
                return { ok: false, message: `${label}: only ${currentStock} in this store, so the amount stays at ${amount}.` };
            }
            setAmount(next);
            return { ok: true, message: `${label}: amount is now ${next}.` };
        }
        setItems(prev => (prev.some(it => it.id === item.id) ? prev : [...prev, item]));
        setSelectedItemId(item.id!);
        setSelectedItem(item);
        setAmount(1);
        setStockLotId(null);
        setSerials([]);
        const extra = item.serialTracked ? ' Pick its serial numbers below.' : item.lotTracked ? ' Pick its lot below.' : '';
        return { ok: true, message: `Selected ${label}, amount 1.${extra}` };
    };

    const handleStoreChange = (id: string) => {
        setSelectedStoreId(id);

        const store = stores.find(store => store.id === id);
        setSelectedStore(store || null);
        setStockLotId(null);
        setSerials([]);   // F6b
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!selectedItemId) {
            toast.showError('Please select an item');
            return;
        }

        if (!selectedStoreId) {
            toast.showError('Please select a store');
            return;
        }

        if (amount <= 0) {
            toast.showError('Please enter a valid amount (greater than 0)');
            return;
        }

        if (currentStock !== null && amount > currentStock) {
            toast.showError(`Stock out amount cannot be greater than available quantity in this store (${currentStock})`);
            return;
        }

        if (isReturnable && !holder && !holderDepartmentId) {
            toast.showError('This is a returnable item: choose the employee or department it is issued to');
            return;
        }

        // ---- F6b: one serial per unit for a serial-tracked item ----
        if (selectedItem?.serialTracked && (!Number.isInteger(amount) || serials.length !== amount)) {
            toast.showError(`Pick ${Number.isInteger(amount) ? amount : 'a whole number of'} serial numbers (one per unit)`);
            return;
        }

        try {
            const response = await StockService.stockOut({
                storeId: selectedStoreId,
                itemId: selectedItemId,
                amount: amount,
                remarks: remarks || undefined,
                stockLotId: stockLotId || undefined,
                buyerOrderId: buyerOrderId || undefined,
                ...(isReturnable ? { holderUserId: holder?.id, holderDepartmentId: holderDepartmentId || undefined } : {}),
                ...(selectedItem?.serialTracked ? { serials } : {}),   // F6b
            });

            // Refresh the current stock after successful stock out
            fetchCurrentStock();

            toast.showSuccess(`Stock out successful. New quantity: ${response.availableQty}`);
            if (response.journalWarning) {
                toast.showError(response.journalWarning);
            }

            // Reset form fields but keep selections
            setAmount(0);
            setRemarks('');
            setStockLotId(null);
            setBuyerOrderId('');
            setSerials([]);   // F6b
            setSerialsNonce(n => n + 1);
            setHolder(null);
            setHolderDepartmentId('');
        } catch (error) {
            console.error('Error during stock out:', error);
            toast.showApiError(error, 'Failed to process stock out');
        }
    };

    if (loading) {
        return <div data-testid="loading-state">Loading...</div>;
    }

    return (
        <Box sx={{ p: 3 }} data-help-page="store.stockOut.page">
            <Typography variant="h4" sx={{ mb: 3 }}>
                Stock Out
            </Typography>
            {storesLoaded && stores.length === 0 && <NotInChargeNotice />}

            <Paper elevation={3} sx={{ p: 3, maxWidth: 720 }}>
            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} data-help-section="store.stockOut.form">
                <Box data-help="store.stockOut.scan">
                <ItemScanField data-testid="stock-out-scan" onItem={handleScan}
                    label="Scan or type item barcode" />
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <SearchableSelect<Item>
                        options={items}
                        value={selectedItemId}
                        onChange={handleItemChange}
                        getOptionLabel={item => `${item.itemName} - Category: ${item.itemCategory?.fullName || item.itemCategory?.itemCategoryName}`}
                        data-help="store.stockOut.field.item" label="Select Item"
                        required
                        fullWidth={false}
                        sx={{ flex: '1 1 260px', minWidth: 0 }}
                        data-testid="item-select"
                    />
                    <SearchableSelect<Store>
                        options={stores}
                        value={selectedStoreId}
                        onChange={handleStoreChange}
                        getOptionLabel={store => store.storeName}
                        data-help="store.stockOut.field.store" label="Select Store"
                        required
                        fullWidth={false}
                        sx={{ flex: '1 1 260px', minWidth: 0 }}
                        data-testid="store-select"
                    />
                </Box>

                {(selectedItem || selectedStore) && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: -1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 260px', minWidth: 0 }} data-testid="item-details">
                            {selectedItem && <>{selectedItem.itemCategory?.fullName || selectedItem.itemCategory?.itemCategoryName}{selectedItem.description ? ` · ${selectedItem.description}` : ''}</>}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ flex: '1 1 260px', minWidth: 0 }} data-testid="store-details">
                            {selectedStore && <>{selectedStore.address || selectedStore.description}</>}
                        </Typography>
                    </Box>
                )}

                {selectedStore && selectedItem && (
                    <Alert severity="info" icon={false} data-testid="current-stock">
                        {loadingStock ? 'Loading current stock…' : (
                            <>Current stock in {selectedStore.storeName}: <strong>{currentStock ?? 0}{selectedItem?.unit ? ` ${selectedItem.unit.symbol}` : ''}</strong></>
                        )}
                    </Alert>
                )}

                {isFixedAsset && (
                    <Alert severity="warning" data-help="store.stockOut.fixedAssetNotice" data-testid="fixed-asset-notice">
                        This item is a fixed asset. Stock Out only reduces stock and expenses it; register the asset in Fixed Assets so it is tracked and depreciated.
                        {canManageAssets && <> <MuiLink component={RouterLink} to="/fixed-assets/new">Register a fixed asset</MuiLink></>}
                    </Alert>
                )}

                {isReturnable && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }} data-help-section="store.stockOut.holder" data-testid="holder-section">
                        <EmployeeAutocomplete
                            label="Issued to (employee)"
                            data-help="store.stockOut.field.holderUser"
                            value={holder}
                            onChange={setHolder}
                            onError={(error) => toast.showApiError(error, 'Failed to search employees')}
                            sx={{ flex: '1 1 260px', minWidth: 0 }}
                            helperText="Returnable item: choose an employee, a department, or both."
                        />
                        <TextField
                            select
                            size="small"
                            label="Issued to (department)"
                            data-help="store.stockOut.field.holderDepartment"
                            value={holderDepartmentId}
                            onChange={(e) => setHolderDepartmentId(e.target.value)}
                            sx={{ flex: '1 1 260px', minWidth: 0 }}
                            data-testid="holder-department-select"
                        >
                            <MenuItem value="">None</MenuItem>
                            {departments.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                        </TextField>
                    </Box>
                )}

                <OrderPicker data-help="store.stockOut.field.order" value={buyerOrderId} onChange={setBuyerOrderId} helperText="Costs this consumption to the order's WIP" />

                {selectedItem?.serialTracked && selectedStoreId && (   /* F6b */
                    <Box>
                        <SerialNumbersField key={serialsNonce} mode="pick" expected={amount} value={serials} onChange={setSerials}
                            itemId={selectedItemId} storeId={selectedStoreId} data-testid="stock-out-serials" />
                    </Box>
                )}

                {selectedItem?.lotTracked && (
                    <Box data-help="store.stockOut.field.lot">
                        <StockLotPicker
                            storeId={selectedStoreId || undefined}
                            itemId={selectedItemId || undefined}
                            value={stockLotId}
                            onChange={setStockLotId}
                            label="Lot"
                        />
                    </Box>
                )}

                <TextField
                    fullWidth
                    data-help="store.stockOut.field.amount" label={`Stock Out Amount${selectedItem?.unit ? ` (${selectedItem.unit.symbol})` : ''}`}
                    type="number"
                    value={amount}
                    onChange={(e) => {
                        const value = Number(e.target.value);
                        if (value >= 0) {
                            setAmount(value);
                        }
                    }}
                    required
                    InputProps={{
                        inputProps: {
                            min: 0,
                            max: currentStock || 0
                        }
                    }}
                    disabled={!selectedItem || !selectedStoreId}
                    data-testid="amount-input"
                />

                <TextField
                    fullWidth
                    data-help="store.stockOut.field.remarks" label="Remarks (optional)"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    multiline
                    rows={2}
                    disabled={!selectedItem || !selectedStoreId}
                    data-testid="remarks-input"
                    inputProps={{ maxLength: TEXT_AREA_MAX_LENGTH }}
                    helperText={`${remarks.length}/${TEXT_AREA_MAX_LENGTH}`}
                />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={!selectedItem || !selectedStoreId}
                    data-help="store.stockOut.submit"
                    {...helpWhy(!selectedItem || !selectedStoreId ? { kind: 'status', message: 'Choose an item and a store first.' } : null)}
                    data-testid="submit-button"
                >
                    Stock Out
                </Button>
                </Box>
            </Box>
            </Paper>
        </Box>
    );
};

export default StockOut;
